/**
 * ImageUploadManager Module
 * Handles image file upload, validation, and management
 */
import type { Analytics } from '../../services/analytics';

export class ImageUploadManager {
    analytics: Analytics;
    backgroundImage: HTMLImageElement | null;
    maxFileSize: number;
    allowedTypes: string[];

    constructor(analytics: Analytics) {
        this.analytics = analytics;
        this.backgroundImage = null;
        this.maxFileSize = 10 * 1024 * 1024; // 10MB
        this.allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        
        this.bindEvents();
    }

    /**
     * Bind upload-related events
     */
    bindEvents(): void {
        const uploadZone = document.getElementById('imageUploadZone');
        const fileInput = document.getElementById('bgImageInput') as HTMLInputElement;
        const removeImageBtn = document.getElementById('removeImageBtn');

        if (!uploadZone || !fileInput) return;

        // Click to upload
        uploadZone.addEventListener('click', () => fileInput.click());
        
        // File input change
        fileInput.addEventListener('change', (e) => this.handleImageUpload(e));
        
        // Drag and drop
        uploadZone.addEventListener('dragover', (e) => {
            e.preventDefault();
            uploadZone.classList.add('dragover');
        });
        
        uploadZone.addEventListener('dragleave', () => {
            uploadZone.classList.remove('dragover');
        });
        
        uploadZone.addEventListener('drop', (e) => {
            e.preventDefault();
            uploadZone.classList.remove('dragover');
            const files = (e as DragEvent).dataTransfer?.files;
            if (files && files.length > 0) {
                this.handleImageFile(files[0]);
            }
        });

        // Image scale mode buttons
        document.querySelectorAll('.scale-option').forEach(button => {
            button.addEventListener('click', (e) => {
                const target = e.target as HTMLElement;
                // Update active state
                document.querySelectorAll('.scale-option').forEach(btn => {
                    btn.classList.toggle('active', (btn as HTMLElement).dataset.scale === target.dataset.scale);
                });
                
                // Track the scale mode change
                this.analytics.trackEvent('image_scale_changed', {
                    scale_mode: target.dataset.scale
                });
                
                // Emit event for preview update
                document.dispatchEvent(new CustomEvent('imageSettingsChanged'));
            });
        });
        
        // Remove image
        if (removeImageBtn) {
            removeImageBtn.addEventListener('click', () => this.removeBackgroundImage());
        }
    }

    /**
     * Handle file input change
     * @param event - Input change event
     */
    handleImageUpload(event: Event): void {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];
        if (file) {
            this.handleImageFile(file);
        }
    }

    /**
     * Process image file
     * @param file - Image file
     */
    handleImageFile(file: File): void {
        // Validate file type
        if (!this.allowedTypes.includes(file.type)) {
            alert('Please select a valid image file (JPEG, PNG, GIF, WebP).');
            return;
        }

        // Validate file size
        if (file.size > this.maxFileSize) {
            alert('File size must be less than 10MB.');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            const result = e.target?.result;
            if (typeof result !== 'string') return;
            
            const img = new Image();
            img.onload = () => {
                this.backgroundImage = img;
                this.showImagePreview(result);
                
                // Emit event for preview update
                document.dispatchEvent(new CustomEvent('imageUploaded', {
                    detail: { image: img }
                }));
                
                // Track upload
                const imageData = {
                    fileSize: file.size,
                    fileType: file.type,
                    width: img.width,
                    height: img.height
                };
                this.analytics.trackBackgroundImageUploaded(imageData);
            };
            img.onerror = () => {
                alert('Failed to load image. Please try a different file.');
            };
            img.src = result;
        };
        reader.onerror = () => {
            alert('Failed to read file. Please try again.');
        };
        reader.readAsDataURL(file);
    }

    /**
     * Show image preview in UI
     * @param imageSrc - Image source URL
     */
    showImagePreview(imageSrc: string): void {
        const uploadZone = document.getElementById('imageUploadZone');
        const previewContainer = document.getElementById('imagePreviewContainer');
        const previewImg = document.getElementById('previewImg') as HTMLImageElement;
        const scaleGroup = document.getElementById('imageScaleGroup');

        if (!uploadZone || !previewContainer || !previewImg) return;

        // Hide upload zone and show preview
        uploadZone.classList.add('hidden');
        previewContainer.classList.remove('hidden');
        
        if (scaleGroup) {
            scaleGroup.classList.remove('hidden');
            
            // Set default scale mode to "contain" (fit)
            document.querySelectorAll('.scale-option').forEach(btn => {
                btn.classList.toggle('active', (btn as HTMLElement).dataset.scale === 'contain');
            });
            
            // Trigger update to apply the fit mode
            document.dispatchEvent(new CustomEvent('imageSettingsChanged'));
        }

        // Set preview image
        previewImg.src = imageSrc;
    }

    /**
     * Remove background image
     */
    removeBackgroundImage(): void {
        const uploadZone = document.getElementById('imageUploadZone');
        const previewContainer = document.getElementById('imagePreviewContainer');
        const scaleGroup = document.getElementById('imageScaleGroup');
        const fileInput = document.getElementById('bgImageInput') as HTMLInputElement;

        // Reset file input
        if (fileInput) {
            fileInput.value = '';
        }

        // Show upload zone and hide preview
        if (uploadZone) {
            uploadZone.classList.remove('hidden');
        }
        if (previewContainer) {
            previewContainer.classList.add('hidden');
        }
        if (scaleGroup) {
            scaleGroup.classList.add('hidden');
        }

        // Clear background image
        this.backgroundImage = null;
        
        // Emit event for preview update
        document.dispatchEvent(new CustomEvent('imageRemoved'));
        
        // Track the removal
        this.analytics.trackBackgroundImageRemoved();
    }

    /**
     * Get current background image
     * @returns Background image or null
     */
    getBackgroundImage(): HTMLImageElement | null {
        return this.backgroundImage;
    }

    /**
     * Check if background image is loaded
     * @returns True if image is loaded
     */
    hasBackgroundImage(): boolean {
        return this.backgroundImage !== null;
    }

    /**
     * Get current image scale mode
     * @returns Scale mode (fill, fit, stretch, repeat)
     */
    getScaleMode(): string {
        const activeScaleButton = document.querySelector('.scale-option.active') as HTMLElement;
        if (!activeScaleButton) return 'fit';
        
        // Map UI scale modes to renderer scale modes
        const scaleMapping: Record<string, string> = {
            'cover': 'fill',
            'contain': 'fit',
            'fill': 'stretch',
            'repeat': 'repeat'
        };
        
        return scaleMapping[activeScaleButton.dataset.scale || 'contain'] || 'fit';
    }

    /**
     * Validate file before processing
     * @param file - File to validate
     * @returns Validation result
     */
    validateFile(file: File): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];
        
        if (!this.allowedTypes.includes(file.type)) {
            errors.push('Invalid file type. Please use JPEG, PNG, GIF, or WebP.');
        }
        
        if (file.size > this.maxFileSize) {
            errors.push('File too large. Please use files smaller than 10MB.');
        }
        
        return {
            isValid: errors.length === 0,
            errors: errors
        };
    }
}
